import { CheckCircleIcon } from '@phosphor-icons/react/dist/ssr/CheckCircle'
import { CubeIcon } from '@phosphor-icons/react/dist/ssr/Cube'
import { FolderSimpleIcon } from '@phosphor-icons/react/dist/ssr/FolderSimple'
import { useTranslations } from 'next-intl'
import { Button, Toast } from '@skop-app/lib-front'

import { isActiveCyneoSubscription } from '@/constants/subscriptions'
import { useUserContext } from '@/context/UserContext'
import { cn, fetcher } from '@/utils'
import { rememberCheckoutReturn } from '@/services/user-subscription/checkoutReturn'

import { resolveCurrentSubscriptionCheckoutLookupKey } from './subscriptionTableTr.checkout'

export const NotCurrentTr = ({
  subscription,
  billingInterval = 'monthly',
  changePlan,
  disabled,
  actionLabel = 'Choisir',
}) => {
  const t = useTranslations()
  const tBilling = useTranslations('Documents-directory')
  const planName = t(subscription.nameKey)
  const paidPrice =
    billingInterval === 'annual'
      ? subscription.priceAnnual
      : subscription.priceMonthly

  return (
    <PlanCard
      planName={planName}
      price={
        subscription.type === 'CUSTOM'
          ? '—'
          : subscription.type === 'FREE'
            ? tBilling(`subscription_free_price_${billingInterval}`)
            : subscription.billingModel === 'per-seat'
              ? t(`Onboarding.offer-plan-price-${billingInterval}`, {
                  price: paidPrice,
                })
              : `${subscription.priceMonthly} € / mois`
      }
      products={subscription.products}
      projects={subscription.projects}
      action={
        <Button
          variant="action"
          disabled={disabled}
          className="w-full"
          onClick={() => changePlan(subscription.type)}
        >
          {actionLabel}
        </Button>
      }
    />
  )
}

/**
 * @param {{ [key: string]: any, quotaLimits?: { PRODUCTS?: number, PROJECTS?: number } }} props
 */
export const CurrentTr = ({
  subscription,
  quotaLimits = undefined,
  currentUserPlan,
  billingInterval,
  disabled,
  legacySubscriptionReadOnly = false,
}) => {
  const t = useTranslations()
  const tBilling = useTranslations('Documents-directory')
  const { team, organizationScope } = useUserContext()
  const isCurrentPlanLegacy = isActiveCyneoSubscription(currentUserPlan)
  const isLegacySubscriptionReadOnly =
    legacySubscriptionReadOnly || isCurrentPlanLegacy
  const planName = isCurrentPlanLegacy
    ? tBilling('subscription_legacy_archived')
    : t(subscription.nameKey)
  const displayedInterval =
    billingInterval ?? subscription.billingInterval ?? 'monthly'
  const paidPrice =
    displayedInterval === 'annual'
      ? subscription.priceAnnual
      : subscription.priceMonthly

  const handleSessionUpdate = async () => {
    if (isLegacySubscriptionReadOnly) return
    if (currentUserPlan?.type === 'CUSTOM') return

    const lookupKey = resolveCurrentSubscriptionCheckoutLookupKey(
      currentUserPlan?.type,
      displayedInterval,
    )
    if (!lookupKey) return

    const activeOrganizationID = organizationScope.activeOrganizationId
    if (!activeOrganizationID) {
      Toast.error(tBilling('subscription_active_team_missing'))
      return
    }

    Toast.loading('Mise à jour en cours...', {
      id: 'updatePlan',
    })

    try {
      const session = await fetcher(
        '/api/stripe/subscription/session',
        'POST',
        {
          lookup_key: lookupKey,
          organizationID: activeOrganizationID,
          organizationName: team?.name,
        },
      )
      if (session?.session) {
        if (session.session.id) {
          rememberCheckoutReturn({
            organizationID: activeOrganizationID,
            sessionID: session.session.id,
          })
        }
        window.location.href = session.session.url
      } else {
        const message =
          "La session Stripe n'a pas pu être créée, veuillez réessayer"
        Toast.error(message)
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? `Erreur: ${error.message}`
          : "La session Stripe n'a pas pu être créée, veuillez réessayer"
      Toast.error(message)
    } finally {
      Toast.dismiss('updatePlan')
    }
  }

  return (
    <PlanCard
      current
      planName={planName}
      badge={
        <>
          <span className="inline-flex items-center gap-1 rounded-md bg-primary-500/15 px-2 py-0.5 text-xs font-medium text-primary-700">
            <CheckCircleIcon className="size-3.5" weight="fill" aria-hidden />
            Formule actuelle
          </span>
          {subscription.status === 'TRIALING' && (
            <span className="rounded-md border border-primary-500/50 bg-white px-2 py-0.5 text-xs text-slate-700">
              Essai
            </span>
          )}
        </>
      }
      price={
        isCurrentPlanLegacy || subscription.type === 'CUSTOM'
          ? '—'
          : subscription.type === 'FREE'
            ? tBilling(`subscription_free_price_${displayedInterval}`)
            : subscription.billingModel === 'fixed' ||
                isLegacySubscriptionReadOnly
              ? `${subscription.priceMonthly} € / mois`
              : t(`Onboarding.offer-plan-price-${displayedInterval}`, {
                  price: paidPrice,
                })
      }
      products={quotaLimits?.PRODUCTS ?? subscription.products}
      projects={quotaLimits?.PROJECTS ?? subscription.projects}
      action={
        <Button
          variant="action"
          className="w-full"
          onClick={handleSessionUpdate}
          disabled={
            disabled ||
            subscription.type === 'FREE' ||
            subscription.type === 'CUSTOM' ||
            isLegacySubscriptionReadOnly
          }
        >
          {isCurrentPlanLegacy ||
          subscription.type === 'FREE' ||
          subscription.type === 'CUSTOM'
            ? 'Actuel'
            : 'Mettre à jour'}
        </Button>
      }
    />
  )
}

const formatAllowance = (value) =>
  typeof value === 'number' && Number.isFinite(value)
    ? value.toLocaleString('fr-FR')
    : '—'

const PlanCard = ({
  planName,
  current = false,
  badge = null,
  price,
  products,
  projects,
  action,
}) => {
  const tBilling = useTranslations('Documents-directory')
  const allowances = [
    {
      key: 'products',
      Icon: CubeIcon,
      label: tBilling('subscription_usage_products'),
      value: products,
    },
    {
      key: 'projects',
      Icon: FolderSimpleIcon,
      label: tBilling('subscription_usage_projects'),
      value: projects,
    },
  ]

  return (
    <li
      className={cn(
        'flex flex-col rounded-md border bg-white p-5 shadow-sm',
        current
          ? 'border-primary-500 ring-1 ring-primary-500'
          : 'border-gray-200',
      )}
      aria-current={current ? 'true' : undefined}
    >
      <div className="flex min-h-6 flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-slate-900">{planName}</h3>
        {badge}
      </div>
      <p className="mt-2 text-lg font-semibold tabular-nums text-slate-900">
        {price}
      </p>
      <ul className="mt-4 flex flex-col gap-2 border-t border-gray-100 pt-4 text-sm text-slate-600">
        {allowances.map(({ key, Icon, label, value }) => (
          <li key={key} className="flex items-center gap-2">
            <Icon className="size-4 text-slate-400" aria-hidden />
            <span className="font-medium tabular-nums text-slate-900">
              {formatAllowance(value)}
            </span>
            <span>{label.toLowerCase()}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto pt-5">{action}</div>
    </li>
  )
}
